import { type ProspectDto, type ProspectTaskDto } from "@crowdcoursing/api/openapi";

import { formatLocalTime, getTimezoneLabel } from "~constants/timezones";
import { type TaskType } from "~hooks/useTaskDashboard";
import { type EventSummary, type RoomTaskSummary } from "~services/graphql/generated/react-query";

// Combined task type for the carousel
export interface CarouselTaskItem extends ProspectTaskDto {
  type: TaskType;
  // Common properties across all task types
  entityId?: string; // ID of the related entity (prospect/room/event)
  entityName?: string; // Name of the related entity
  entityImage?: string; // Image URL of the related entity
  entityType?: string; // Type of entity (individual, organization, etc.)
  category?: string; // Task category (meeting, follow-up, document, etc.)
  tags?: string[]; // Tags for filtering and organization
  chips?: string[]; // Additional chips to display (e.g., Region, Time Zone for sales tasks)
  isRecurring?: boolean; // Whether the task repeats
  relatedUsers?: {
    // Users related to this task
    id: string;
    name: string;
    role: string;
  }[];
}

/** Prospect data used for enriching sales tasks with additional info */
export type ProspectEnrichmentData = Pick<ProspectDto, "id" | "region" | "timeZone">;

/**
 * Map sales tasks to the common format
 * @param task - The prospect task DTO
 * @param prospectData - Optional prospect data for enrichment (region, timeZone)
 */
export const mapSalesTask = (task: ProspectTaskDto, prospectData?: ProspectEnrichmentData): CarouselTaskItem => {
  // Extract tags from description
  const tags: string[] = [];
  if (task.description) {
    // Look for hashtags in description
    const hashtagRegex = /#(\w+)/g;
    let match;
    while ((match = hashtagRegex.exec(task.description)) !== null) {
      tags.push(match[1]);
    }
  }

  // Build chips array from prospect data (Region and Time Zone for sales tasks)
  const chips: string[] = [];
  if (prospectData) {
    if (prospectData.region) {
      chips.push(prospectData.region);
    }
    if (prospectData.timeZone) {
      // Show timezone label with local time (e.g., "Eastern Time (ET) • 2:30 PM")
      const label = getTimezoneLabel(prospectData.timeZone);
      const localTime = formatLocalTime(prospectData.timeZone);
      chips.push(localTime ? `${label} • ${localTime}` : label);
    }
  }

  return {
    ...task,
    chips,
    entityId: task.prospectId,
    entityName: task.prospectName || "Unknown Prospect",
    entityType: "Prospect",
    isRecurring: false,
    // Default for now
    relatedUsers: task.assignedToFirstName
      ? [
          {
            id: task.assignedToUserId || "",
            name: `${task.assignedToFirstName} ${task.assignedToLastName || ""}`.trim(),
            role: "Assigned",
          },
        ]
      : [],

    tags,
    type: "sales" as TaskType,
  };
};

// Map room tasks to the common format (placeholder)
export const mapRoomTask = (task: RoomTaskSummary): CarouselTaskItem => {
  return {
    ...task,
    category: "Room Task",
    entityId: task.roomId,
    entityName: task.name || "Unknown Room",
    entityType: "Room",
    tags: [],
    type: "room" as TaskType,
  };
};

// Map event tasks to the common format (placeholder)
export const mapEventTask = (task: EventSummary): CarouselTaskItem => {
  return {
    ...task,
    category: "Event Task",
    entityId: task.id,
    entityName: task.name || "Unknown Event",
    entityType: "Event",
    tags: [],
    type: "event" as TaskType,
  };
};

/**
 * Combine tasks from different sources into a unified format
 * @param salesTasks - Array of sales/prospect tasks
 * @param roomTasks - Array of room tasks
 * @param eventTasks - Array of event tasks
 * @param prospectsMap - Optional map of prospect ID to enrichment data (region, timeZone)
 */
export const combineTasks = (
  salesTasks: ProspectTaskDto[] = [],
  roomTasks: RoomTaskSummary[] = [],
  eventTasks: EventSummary[] = [],
  prospectsMap?: Map<string, ProspectEnrichmentData>,
): CarouselTaskItem[] => {
  return [
    ...salesTasks.map((task) => {
      const prospectData = task.prospectId && prospectsMap ? prospectsMap.get(task.prospectId) : undefined;
      return mapSalesTask(task, prospectData);
    }),
    ...roomTasks.map(mapRoomTask),
    ...eventTasks.map(mapEventTask),
  ];
};
